import decode from 'jwt-decode';
import { format } from 'date-fns';
import { find, get, intersection, memoize } from 'lodash';
import { mapProps } from 'recompose';
import * as moment from 'moment';
import TextField from 'material-ui/TextField';

import * as storage from '@inqool/uas-web-core/es/utils/storage';
import { PeriodTypesEnum } from '@modules/hr-templates/periods/_periodsEnums';

import customFetch from './fetch';

export const DATE_FORMAT = 'YYYY-MM-DD';

const checkToken = memoize(token => {
  try {
    decode(token);
    return true;
  } catch (e) {
    console.error('fetch', e);
    return false;
  }
});

export const fetch = (url, options = {}) => {
  const tokenOptions = {
    checkToken,
    ...options
  };
  return customFetch(url, tokenOptions);
};

export const periodTypeLabel = type => get(find(PeriodTypesEnum, { id: type }), 'label', '');

export const stringifyObject = object => {
  let result = '';

  for (const char of Object.keys(object)) {
    result += typeof object[char] === 'string' ? object[char] : '';
  }
  return result;
};

export const getActualDate = (stringFormat = 'YYYY-MM-DD[T]HH:mm') => format(new Date(), stringFormat);

export const maxTextLength = (text, maxLen) => {
  if (typeof text === 'string') {
    return text.length > maxLen ? text.slice(0, maxLen - 3).concat('...') : text;
  }
  return text;
};

export const buildFullAddress = formData => {
  const { street = '', descriptiveNumber = '', orientationNumber = '', zip = '', municipality = '' } = formData;
  const separators = [' ', '/', ', ', ' '];

  // Replacing (removing) separators if corresponding attribute(s) is/are missing
  if (!street) {
    separators[0] = '';
  }
  if (!descriptiveNumber || !orientationNumber) {
    separators[1] = '';
    if (!descriptiveNumber && !orientationNumber) {
      separators[0] = '';
      if (!street) {
        separators[2] = '';
      }
    }
  }
  if (!zip || !municipality) {
    separators[3] = '';
  }

  return `${street}${separators[0]}${descriptiveNumber}${separators[1]}${orientationNumber}${separators[2]}${zip}${
    separators[3]
  }${municipality}`;
};

export const integerOnly = value => value && value.replace(/\D/g, '');
export const floatOnly = value =>
  value
    .toString()
    .replace(/[^\d\,]/g, '')
    .replace(/\,/, 'x')
    .replace(/\,/g, '')
    .replace(/x/, ',')
    .replace(',', '.');

export const formatFloat = value => (value === 0 ? '0' : !value ? '' : value.toString().replace('.', ','));

export const formNumberInputWrapper = mapProps<any, any>(formProps => {
  const { input, meta, label, ...rest } = formProps;
  return {
    errorText: meta.touched && meta.error,
    value: input.value,
    onChange: input.onChange,
    name: input.name,
    floatingLabelText: label,
    fullWidth: true,
    thousandSeparator: ' ',
    isNumericString: true,
    decimalSeparator: ',',
    customInput: TextField,
    allowNegative: false,
    ...rest
  };
});

export const filterByModulesAndPermissions = (modules: string[], items: any[] = []) => {
  const myPermissions = JSON.parse(storage.get('permissions'));
  const result = items.filter(({ moduleCodes, permissions }: any) => {
    const filterValue =
      (!moduleCodes || !!intersection(modules, moduleCodes).length) &&
      (!permissions || !!intersection(myPermissions, permissions).length);

    return filterValue;
  });

  return result;
};

export const hasAnyPermission = (permissions: Array<string> | undefined): boolean => {
  const myPermissions = JSON.parse(storage.get('permissions'));
  return permissions ? intersection(myPermissions, permissions).length !== 0 : true;
};

export const setMds = (length: number): Array<number> => {
  switch (length) {
    case 1:
      return [12];
    default:
      switch (length % 3) {
        case 1:
          return [...Array(4).fill(6), ...Array(length - 4).fill(4)];
        case 2:
          return [...Array(2).fill(6), ...Array(length - 2).fill(4)];
        default:
          return Array(length).fill(4);
      }
  }
};

export const formatDate = (date: string) => moment(date).format('DD. MM. YYYY');

export const reportItemsTranformator = (
  items: Record<string, [string, string]> = {}
): Array<{ label: string; value: string }> => {
  const values = Object.keys(items);
  const newItems = values.map(value => ({ label: items[value][0], value }));
  return newItems;
};

export const formatToDecimals = (value: any, decimals = 2): string => {
  const num = parseFloat(value);
  if (isNaN(num)) {
    return '';
  }
  if (num === 0) {
    return '0';
  }
  return formatFloat(num.toFixed(decimals));
};

export const birthNumberToBirthDate = (birthNumber: string): Date | null => {
  birthNumber = birthNumber.replace(/[^0-9\/]|\s+/g, '');

  let year = parseInt(birthNumber.slice(0, 2), 10);
  let month = parseInt(birthNumber.slice(2, 4), 10);
  const day = parseInt(birthNumber.slice(4, 6), 10);

  let century;
  if (birthNumber.length === 9) {
    century = 1900; // 1900-1953
  } else if (year >= 54 && year <= 99) {
    century = 1900; // 1954-1999
  } else {
    century = 2000; // 2000+
  }

  // some women have 50 added to month
  if (month > 50) {
    month -= 50;
  }

  // if year is in 20th century, add 1900 or 2000 according to rule above
  year += century;

  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return null;
  }

  return new Date(year, month - 1, day);
};

export const parseBool = (value: string | boolean) => {
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return value;
};



// WEBPACK FOOTER //
// ./src/utils/utils.ts